import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { finalize } from 'rxjs/operators';
import { KnowledgeService } from '../../../core/services/api/knowledge.service';
import { KnowledgeArticle, KnowledgeRelatedLink } from '../../../core/models/learn/knowledge-article';
import { HeaderService } from '../../../shared/services/header/header.service';
import { KnowledgeSeoService } from '../../../core/services/seo/knowledge-seo.service';

/** One on-this-page outline entry, built from the article's h2/h3 headings. */
interface TocItem {
  id: string;
  text: string;
  level: number; // 2 = h2, 3 = h3
}

/**
 * Native pitchIN Learn/FAQ reader (#48), sourced from the Payload CMS via the API content
 * proxy (#33). Redesigned into the variant-C 3-column template: a category tree, the article
 * content, and an on-this-page outline (TOC). Shows breadcrumbs, author + last-updated, related
 * Learn↔FAQ cross-links, and a persisted "Was this helpful?" control. Members-only content renders
 * as a teaser + sign-up-to-unlock for anonymous readers; signed-in members see it in full.
 */
@Component({
  selector: 'app-knowledge-article',
  templateUrl: './knowledge-article.component.html',
  styleUrls: ['./knowledge-article.component.scss'],
  standalone: false,
})
export class KnowledgeArticleComponent implements OnInit, OnDestroy {
  slug: string;
  isFaq = false;
  article: KnowledgeArticle;
  content: SafeHtml;
  toc: TocItem[] = [];
  isLoading = true;
  notFound = false;
  helpfulChoice: 'yes' | 'no' | null = null;
  helpfulSubmitting = false;

  /** #51 SEO JSON-LD, server-rendered into the head via <app-json-ld> in the template. */
  articleLd: { [key: string]: any } | null = null;
  breadcrumbLd: { [key: string]: any } | null = null;

  constructor(
    private route: ActivatedRoute,
    private knowledgeService: KnowledgeService,
    private sanitizer: DomSanitizer,
    private headerService: HeaderService,
    private seo: KnowledgeSeoService,
  ) {}

  get collection(): 'learn' | 'faq' {
    return this.isFaq ? 'faq' : 'learn';
  }

  /** Breadcrumb trail: Knowledge Centre / Learn|FAQ / <title>. */
  get breadcrumb(): string[] {
    const trail = ['Knowledge Centre', this.isFaq ? 'FAQ' : 'Learn'];
    if (this.article?.title) {
      trail.push(this.article.title);
    }
    return trail;
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.slug = params.get('slug');
      this.isFaq = this.route.snapshot.data?.['collection'] === 'faq';
      this.load();
    });
  }

  private load(): void {
    this.isLoading = true;
    this.notFound = false;
    this.helpfulChoice = null;
    this.toc = [];
    this.articleLd = null;
    this.breadcrumbLd = null;
    this.seo.clearManagedLinks();

    const request$ = this.isFaq
      ? this.knowledgeService.getFaq(this.slug)
      : this.knowledgeService.getArticle(this.slug);

    request$.pipe(finalize(() => (this.isLoading = false))).subscribe({
      next: (article) => {
        this.article = article;
        // The reader renders the title as the page H1, so drop the body's own leading title
        // heading (CMS content often opens with `# <title>`) to avoid showing it twice.
        const body = this.stripLeadingTitle(article?.html || '', article?.title);
        const withAnchors = this.buildTocAndAnchor(body);
        // Safe: `article.html` is server-sanitised by the API content-proxy (#33) — Lexical is
        // rendered to HTML there with a scheme allow-list, over maker-checker-approved CMS content.
        // The bypass is required only so Angular's sanitiser doesn't strip the heading `id` anchors
        // that buildTocAndAnchor() injects for the on-this-page TOC. NOSONAR: S6268 by design.
        this.content = this.sanitizer.bypassSecurityTrustHtml(withAnchors); // NOSONAR
        if (article?.title) {
          this.headerService.setHeaderTag(`${article.title} | pitchIN`);
        }
        this.applySeo(article);
      },
      error: () => {
        // proxy returns code NOTFOUND for missing/unpublished/internal articles
        this.notFound = true;
      },
    });
  }

  /**
   * Builds the #51 SEO surface for this article: the Article/TechArticle + BreadcrumbList JSON-LD
   * (rendered into the head by <app-json-ld> → present in view-source) and the self-referencing
   * canonical + hreflang scaffolding. No FAQPage/HowTo markup is emitted (retired May 2026).
   */
  private applySeo(article: KnowledgeArticle): void {
    if (!article) {
      return;
    }
    this.articleLd = this.seo.buildArticleLd(article, this.collection);
    this.breadcrumbLd = this.seo.buildBreadcrumbLd(this.breadcrumb, article, this.collection);
    const url = this.seo.articleUrl(article, this.collection);
    this.seo.setCanonicalAndHreflang(url, article.language);
  }

  ngOnDestroy(): void {
    // Leaving the reader: drop the canonical/hreflang tags so the next route starts clean.
    this.seo.clearManagedLinks();
  }

  /**
   * Drops a leading <h1> whose text equals the article title — the reader shows the title as the
   * page heading, and CMS bodies often open with `# <title>`, which would render it twice. Only
   * a matching leading H1 is removed (genuine in-body H1s are left alone). SSR-safe regex.
   */
  private stripLeadingTitle(html: string, title?: string): string {
    if (!html || !title) {
      return html;
    }
    const match = html.match(/^\s*<h1\b[^>]*>([\s\S]*?)<\/h1>/i);
    if (match) {
      const headingText = match[1].replace(/<[^>]+>/g, '').trim();
      if (headingText.toLowerCase() === title.trim().toLowerCase()) {
        return html.slice(match[0].length);
      }
    }
    return html;
  }

  /**
   * Parses h2/h3 headings out of the rendered HTML, gives each a stable slug id (so TOC links can
   * deep-link/scroll to a section), and returns the HTML with those ids injected. Pure string work
   * (regex) so it is SSR-safe — no DOM dependency. Populates {@link toc}.
   */
  private buildTocAndAnchor(html: string): string {
    if (!html) {
      return html;
    }

    const used = new Set<string>();
    this.toc = [];

    return html.replace(/<(h[23])([^>]*)>([\s\S]*?)<\/\1>/gi, (match, tag, attrs, inner) => {
      const text = inner.replace(/<[^>]+>/g, '').trim();
      if (!text) {
        return match;
      }
      const id = this.uniqueSlug(text, used);
      this.toc.push({ id, text, level: tag.toLowerCase() === 'h3' ? 3 : 2 });
      // Preserve any existing attributes; add the id (the renderer does not currently set one).
      return `<${tag}${attrs} id="${id}">${inner}</${tag}>`;
    });
  }

  private uniqueSlug(text: string, used: Set<string>): string {
    const base =
      'section-' +
      text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 50);
    let id = base || 'section';
    let n = 2;
    while (used.has(id)) {
      id = `${base}-${n++}`;
    }
    used.add(id);
    return id;
  }

  scrollToSection(id: string, event: Event): void {
    event.preventDefault();
    if (typeof document === 'undefined') {
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /** Route path for a related cross-link, by collection (learn → read, faq → faq). */
  relatedRoute(link: KnowledgeRelatedLink): string[] {
    return link.type === 'faq' ? ['/learn/faq', link.slug] : ['/learn/read', link.slug];
  }

  voteHelpful(choice: 'yes' | 'no'): void {
    if (this.helpfulChoice !== null || this.helpfulSubmitting) {
      return;
    }
    this.helpfulChoice = choice;
    this.helpfulSubmitting = true;

    // Persist server-side (#48): the proxy writes the vote to Postgres (tracked insert).
    this.knowledgeService
      .submitFeedback(this.slug, this.collection, choice === 'yes')
      .pipe(finalize(() => (this.helpfulSubmitting = false)))
      .subscribe({
        // Optimistic UI: the thank-you state is already shown; nothing more to do on success.
        next: () => {},
        // If the write fails, keep the thank-you state — the vote is best-effort and never blocks the reader.
        error: () => {},
      });
  }
}
